import React, { useState } from 'react';
import s from './BrokerProgram.module.sass';
import paint from '../../img/paint.png';
import { useSimpleTranslation } from '../../hooks/useTranslation';

export default function FintechSuccessModal({
    isOpen,
    onClose,
    currency = 'RM',
    amount = '0.00',
    name = '',
    bank = '',
    bankIconSrc,
    accountLabel = 'Счет:',
    accountValue = '',
    initialVisible = true,
    paymentDate = '',
    txId = '',
    siteSettings,
    onTxIdRegenerate
}) {
    const { t } = useSimpleTranslation(siteSettings || {});
    // В режиме отзовика по умолчанию данные скрыты
    const defaultVisible = siteSettings?.otzovikMode ? false : Boolean(initialVisible);
    const [isVisible, setIsVisible] = useState(defaultVisible);
    const [localTxId, setLocalTxId] = useState(txId);

    // Обновляем localTxId при изменении txId из пропсов
    React.useEffect(() => {
        setLocalTxId(txId);
    }, [txId]);

    // В режиме отзовика всегда скрываем данные по умолчанию при открытии модала
    React.useEffect(() => {
        if (siteSettings?.otzovikMode && isOpen) {
            setIsVisible(false);
        }
    }, [isOpen, siteSettings?.otzovikMode]);

    if (!isOpen) return null;

    const generateTxId = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = 'TX';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleTxIdClick = () => {
        if (siteSettings?.otzovikMode) {
            const newTxId = generateTxId();
            setLocalTxId(newTxId);
            if (onTxIdRegenerate) {
                onTxIdRegenerate(newTxId);
            }
        }
    };

    const renderAmountSplit = (value) => {
        if (!value || typeof value !== 'string') return value;
        // разделяем по последнему разделителю десятых (точка или запятая)
        const lastDot = value.lastIndexOf('.');
        const lastComma = value.lastIndexOf(',');
        let sepIndex = Math.max(lastDot, lastComma);
        if (sepIndex === -1) return value;
        const intPart = value.slice(0, sepIndex);
        const fracPart = value.slice(sepIndex + 1);
        return (<>
            {intPart}
            <sup className={s.fintechCents}>{fracPart}</sup>
        </>);
    };

    return (
        <div className={s.fintechModalOverlay} onClick={onClose}>
            <div className={s.fintechModalContainer} onClick={(e) => e.stopPropagation()}>
                {/* Header like BrokerProgram */}
                <div className={s.fintechModalHeader}>
                    <div className={s.fintechLogoContainer}>
                        <img 
                            className={s.fintechLogo} 
                            src={siteSettings?.siteLogo 
                                ? (siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http') 
                                    ? siteSettings.siteLogo 
                                    : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`)
                                : paint} 
                            alt="logo"
                            onError={(e) => {
                                console.error('[FintechSuccessModal] Ошибка загрузки логотипа:', e.target.src);
                                if (!e.target.src.includes('/data/logo/')) {
                                    const filename = siteSettings?.siteLogo;
                                    if (filename) {
                                        e.target.src = `/data/logo/${encodeURIComponent(filename)}`;
                                    } else {
                                        e.target.src = paint;
                                    }
                                } else {
                                    e.target.src = paint;
                                }
                            }}
                        />
                        <span 
                            className={s.fintechBrandText}
                            style={{ 
                                fontFamily: siteSettings?.siteTitleFont || "'Inter', sans-serif",
                                fontWeight: siteSettings?.siteTitleFontWeight || '400',
                                fontStyle: siteSettings?.siteTitleFontStyle || 'normal',
                                letterSpacing: (() => {
                                    const spacing = siteSettings?.siteTitleLetterSpacing;
                                    if (spacing && spacing.trim() !== '') {
                                        const value = spacing.trim();
                                        if (!isNaN(parseFloat(value)) && !value.match(/px|em|rem|pt|%/)) {
                                            return value + 'px';
                                        }
                                        return value;
                                    }
                                    return 'normal';
                                })(),
                                fontSize: (() => {
                                    const size = siteSettings?.siteTitleFontSize;
                                    if (size && size.trim() !== '') {
                                        const value = size.trim();
                                        if (!isNaN(parseFloat(value)) && !value.match(/px|em|rem|pt|%/)) {
                                            return value + 'px';
                                        }
                                        return value;
                                    }
                                    return undefined;
                                })()
                            }}
                        >
                            {siteSettings?.siteName || '暗号交換'}
                        </span>
                    </div>
                    <button className={s.fintechModalClose} onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {/* TX info like BrokerProgram */}
                <div className={s.fintechTxInfoBlock}>
                    <div className={s.fintechTxLeft}>
                        <div 
                            className={s.fintechTxId}
                            onClick={handleTxIdClick}
                            style={{ cursor: siteSettings?.otzovikMode ? 'pointer' : 'default' }}
                        >
                            #{localTxId}
                        </div>
                        {paymentDate ? (
                            <div className={s.fintechTxTime}>
                                <i className="fas fa-calendar-alt" style={{ marginRight: '8px', fontSize: '12px' }}></i>
                                {paymentDate}
                            </div>
                        ) : null}
                    </div>
                    <div className={s.fintechTxRight}>
                        <div className={`${s.fintechStatusBadge} ${s['success']}`}>
                            <div className={`${s.statusCircle} ${s['success']}`}></div>
                            <span>{t('brokerProgram.statuses.success', 'Успешно')}</span>
                        </div>
                    </div>
                </div>

                <div className={`${s.fintechAmountSection} ${s.success}`}>
                    <div className={s.fintechAmountLabel}>
                        <i className="fas fa-paper-plane" style={{ marginRight: '8px', fontSize: '14px' }}></i>
                        {t('modals.success.amountSent','Сумма отправлена')}
                    </div>
                    <div className={s.fintechAmountValue}>
                        <span className={s.fintechCurrency}>{currency}</span>
                        <span className={s.fintechAmount}>{renderAmountSplit(amount)}</span>
                    </div>
                </div>

                <div className={s.fintechUserDataBlock}>
                    <div className={s.fintechUserDataTitle}>
                        <i className="fas fa-user-circle" style={{ marginRight: '8px', fontSize: '16px' }}></i>
                        {t('modals.success.recipientDetails','Данные получателя')}
                    </div>

                    <div className={s.fintechDataContainer}>
                        <div className={s.fintechDataItem}>
                            <div className={s.fintechDataLabel}>
                                <i className="fas fa-user" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                {t('modals.success.userDetails.name','Имя:')}
                            </div>
                            <div className={s.fintechDataValue}>{name}</div>
                        </div>

                        <div className={s.fintechDataDivider}></div>

                        <div className={s.fintechDataItem}>
                            <div className={s.fintechDataLabel}>
                                <i className="fas fa-university" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                {t('modals.success.userDetails.bank','Банк:')}
                            </div>
                            <div className={s.fintechDataValueWithIcon}>
                                <span>{bank}</span>
                                {bankIconSrc && (
                                    <img
                                        src={bankIconSrc}
                                        alt={bank}
                                        className={s.fintechBankIcon}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={s.fintechDataDivider}></div>

                        <div className={s.fintechDataItem}>
                            <div className={s.fintechDataLabel}>
                                <i className="fas fa-wallet" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                {`${accountLabel}`}
                            </div>
                            <div className={s.fintechAccountRow}>
                                <span className={s.fintechAccountNumber}>
                                    {isVisible 
                                        ? accountValue 
                                        : (() => {
                                            // Всегда показываем 4 звездочки и только 4 последних символа
                                            if (!accountValue || accountValue.length <= 4) {
                                                return accountValue;
                                            }
                                            const lastFour = accountValue.slice(-4);
                                            return '****' + lastFour;
                                        })()
                                    }
                                </span>
                                <button className={s.fintechEyeButton} onClick={() => setIsVisible(!isVisible)}>
                                    {/* Глазик: открытый когда данные видимы, закрытый когда скрыты */}
                                    {isVisible ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 12C3 7 7.5 4 12 4s9 3 11 8c-2 5-6.5 8-11 8S3 17 1 12Z" stroke="currentColor" strokeWidth="2"/>
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={s.fintechModalFooter}>
                    <button className={s.fintechCloseButton} onClick={onClose}>{t('modals.success.close','Закрыть')}</button>
                </div>
            </div>
        </div>
    );
}


